/**
 * Payment Success Page
 * Confirmation page after successful course enrollment
 */
import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { 
  CheckCircle, BookOpen, PlayCircle, Download, 
  Mail, ArrowRight, Sparkles, Award 
} from 'lucide-react';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { paymentId, courseId, courseName } = location.state || {};

  useEffect(() => {
    // If no payment data, redirect to courses
    if (!paymentId || !courseId) {
      navigate('/courses');
    }
  }, [paymentId, courseId, navigate]);

  if (!paymentId || !courseId) {
    return null;
  }

  return (
    <>
      <SEO 
        title="Payment Successful - Welcome to DevOps Training!"
        description="Your course enrollment is complete. Start your DevOps learning journey with hands-on labs and expert guidance."
        keywords="payment success, course enrolled, DevOps training access, lab platform"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          
          {/* Success Animation */}
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-yellow-400 animate-bounce" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-2">
              Payment Successful! 🎉
            </h1>
            <p className="text-lg text-slate-600 mb-4">
              Welcome to your DevOps learning journey
            </p>
            <Badge className="bg-emerald-100 text-emerald-800 px-4 py-2">
              Course Access Granted
            </Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Payment Confirmation */}
            <Card className="bg-white shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800">Enrollment Confirmed</h3>
                    <p className="text-slate-600">You're now enrolled in</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-semibold text-lg mb-1">
                        {courseId === 'RHCSA_COMPLETE' ? 'RHCSA Complete Bundle' : courseName}
                      </h4>
                      <Badge className="bg-white/20 text-white">
                        {courseId === 'RHCSA_COMPLETE' ? 'RH124 + RH134 Bundle' : `${courseId} Course`}
                      </Badge>
                    </div>
                    {courseId === 'RHCSA_COMPLETE' && (
                      <div className="text-right">
                        <div className="text-white/80 text-xs">Bundle Access</div>
                        <div className="text-white font-bold">60 Labs</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Payment ID:</span>
                    <span className="font-mono text-slate-800">{paymentId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Course ID:</span>
                    <span className="font-semibold text-slate-800">{courseId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Status:</span>
                    <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Steps */}
            <Card className="bg-white shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-slate-800 mb-6">What's Next?</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <PlayCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-slate-800">Start Learning Immediately</h4>
                      <p className="text-sm text-slate-600">
                        {courseId === 'RHCSA_COMPLETE' 
                          ? 'Access all RH124 & RH134 labs - your complete RHCSA journey begins now'
                          : 'Access your labs and begin hands-on practice'
                        }
                      </p>
                      {courseId === 'RHCSA_COMPLETE' && (
                        <div className="text-xs text-emerald-700 mt-1 font-medium">
                          ✅ 60 Labs • ✅ Both Courses Unlocked
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Mail className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-slate-800">Check Your Email</h4>
                      <p className="text-sm text-slate-600">Confirmation and learning roadmap sent to your inbox</p>
                      <div className="text-xs text-blue-700 mt-1">Receipt and course access details included</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Award className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-slate-800">
                        {courseId === 'RHCSA_COMPLETE' ? 'RHCSA Certification Ready' : 'Earn Certificate'}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {courseId === 'RHCSA_COMPLETE'
                          ? 'Complete both courses to be RHCSA exam ready with industry-recognized skills'
                          : 'Complete labs to earn your certification'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
            <Button 
              asChild
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 py-3 text-lg min-h-[48px] touch-manipulation shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
            >
              <Link to={courseId === 'RHCSA_COMPLETE' ? "/courses/RH124" : `/courses/${courseId}`}>
                <PlayCircle className="mr-2 h-5 w-5" />
                {courseId === 'RHCSA_COMPLETE' ? 'Start RH124 Journey' : 'Start Learning Now'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            
            {courseId === 'RHCSA_COMPLETE' && (
              <Button 
                asChild
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8 py-3 text-lg min-h-[48px] touch-manipulation shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
              >
                <Link to="/courses/RH134">
                  <Award className="mr-2 h-5 w-5" />
                  Access RH134 Too
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            )}
            
            <Button 
              asChild
              variant="outline"
              className="px-8 py-3 text-lg min-h-[48px] touch-manipulation hover:bg-gray-50"
            >
              <Link to="/dashboard">
                <BookOpen className="mr-2 h-5 w-5" />
                View Dashboard
              </Link>
            </Button>
          </div>

          {/* Unlock More Courses - Member Pricing Banner */}
          <div className="mt-8 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border border-amber-200 rounded-2xl p-6 shadow-sm">
            <div className="text-center">
              <div className="inline-flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 p-3 rounded-xl shadow-lg mb-4">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-bold text-slate-800 text-xl mb-2">
                🎁 You've Unlocked Member Pricing!
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                As a GRRAS member, you now get special discounted rates on all additional courses - including future ones!
              </p>
              <Link 
                to="/pricing"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                View All Courses & Special Rates
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Social Proof & Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 mb-8">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-emerald-600">5000+</div>
              <div className="text-sm text-gray-600">Students Enrolled</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-blue-600">4.8/5</div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <div className="text-2xl font-bold text-purple-600">85%</div>
              <div className="text-sm text-gray-600">Job Placement Rate</div>
            </div>
          </div>

          {/* Support Info */}
          <div className="text-center mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-slate-800 mb-2">Need Help Getting Started?</h4>
            <p className="text-slate-600 text-sm mb-4">
              Our expert support team is here to help you succeed in your {courseId === 'RHCSA_COMPLETE' ? 'RHCSA certification' : 'DevOps'} journey
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm">
              <a href="mailto:info@grras.tech" className="text-blue-600 hover:underline font-medium">
                📧 info@grras.tech
              </a>
              <span className="text-gray-300 hidden sm:block">|</span>
              <div className="text-slate-600">
                💬 Live Chat Support • 📚 Learning Resources Available
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccess;